import {
	ApiResponse,
	ArticleInsights,
	AvailabilityData,
	BaseBettingType,
	BettingData,
	EventInsightsAvailability,
	EventInsightsData,
	ILivePlayerStatsAvailability,
	ILivePlayerStatsResponse,
	IMatchStatsAvailability,
	IMatchStatsResponse,
	PlayerStatsData,
	StatsRequestModel,
	StreakStatsAvailabilityResponse,
	StreakStatsRequestModel,
	StreakStatsResponse,
	TeamStandingsRequestModel,
	TeamStandingsApiResponse,
	TeamStandingsResponse,
	TeamStandingsResponseAvailability,
} from "@data-types/sport-stats/SportStatsTypes";
import { httpClient } from "@utils";
import { getApiRequestContext } from "@utils/api-config";
import { getApplicationRequestHeaders } from "@utils/api-config/getApplicationRequestHeader";

export default class SportsStatsApiService {
	static get baseUrl() {
		return `${WEBPACK_SPORT_STATISTICS_API_ADDRESS}/sport-stats/gen`;
	}

	static getRequestHeaders = (context = getApiRequestContext()) => {
		const appHeaders = getApplicationRequestHeaders(context);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	};

	static async fetchTeamBettingTypeStatistics<T extends StatsRequestModel>(
		data: T
	): Promise<
		T["specific"]["RequestType"] extends "data"
			? ApiResponse<BettingData>
			: ApiResponse<BaseBettingType & { data: AvailabilityData }>
	> {
		return await httpClient.post(
			`${this.baseUrl}/teambettingtype/data`,
			JSON.stringify(data),
			this.getRequestHeaders()
		);
	}

	static async fetchPlayerTypeStatistics<T extends StatsRequestModel>(
		data: T
	): Promise<
		T["specific"]["RequestType"] extends "data"
			? ApiResponse<PlayerStatsData>
			: ApiResponse<BaseBettingType & { data: AvailabilityData }>
	> {
		return await httpClient.post(
			`${this.baseUrl}/playerbettingtype/data`,
			JSON.stringify(data),
			this.getRequestHeaders()
		);
	}

	static async fetchMatchStatistics<T extends StatsRequestModel>(
		data: T
	): Promise<
		T["specific"]["RequestType"] extends "data"
			? ApiResponse<IMatchStatsResponse>
			: ApiResponse<IMatchStatsAvailability>
	> {
		return await httpClient.post(
			`${this.baseUrl}/live/data`,
			JSON.stringify(data),
			this.getRequestHeaders()
		);
	}

	static async fetchPlayerLiveStatistics<T extends StatsRequestModel>(
		data: T
	): Promise<
		T["specific"]["RequestType"] extends "data"
			? ApiResponse<ILivePlayerStatsResponse>
			: ApiResponse<ILivePlayerStatsAvailability>
	> {
		return await httpClient.post(
			`${this.baseUrl}/eventPlayers/data`,
			JSON.stringify(data),
			this.getRequestHeaders()
		);
	}

	static async fetchStreakStatistics<T extends StreakStatsRequestModel>(
		data: T
	): Promise<
		T["specific"]["RequestType"] extends "data"
			? ApiResponse<StreakStatsResponse>
			: ApiResponse<StreakStatsAvailabilityResponse>
	> {
		return await httpClient.post(
			`${this.baseUrl}/teamstreak/data`,
			JSON.stringify(data),
			this.getRequestHeaders()
		);
	}

	static async fetchEventInsights<T extends StatsRequestModel>(
		data: T
	): Promise<
		T["specific"]["RequestType"] extends "data"
			? ApiResponse<EventInsightsData>
			: ApiResponse<EventInsightsAvailability>
	> {
		return await httpClient.post(
			`${this.baseUrl}/insight/data`,
			JSON.stringify(data),
			this.getRequestHeaders()
		);
	}

	static async fetchArticleInsights(
		eventId: string
	): Promise<ArticleInsights> {
		return await httpClient.get(
			`${WEBPACK_SPORT_ARTICLE_API_ADDRESS}/article-gen/article/${eventId}`,
			this.getRequestHeaders(),
			{ responseType: "json" }
		);
	}

	static async fetchTeamStandingsStatistic<
		T extends TeamStandingsRequestModel,
	>(
		data: T
	): Promise<
		T["specific"]["RequestType"] extends "data"
			? TeamStandingsApiResponse<TeamStandingsResponse>
			: ApiResponse<TeamStandingsResponseAvailability>
	> {
		return await httpClient.post(
			`${this.baseUrl}/TeamStanding/data`,
			JSON.stringify(data),
			this.getRequestHeaders(),
			{ responseType: "json" }
		);
	}
}
